import { Plus, Upload, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import { getFixFecha, subirFacturaExtranjera } from "@/lib/api";

/**
 * Captura manual de una factura emitida en el EXTRANJERO (sin CFDI), con su PDF.
 *
 * Sustituye lo que en un CFDI viene del XML: el emisor no tiene RFC (país +
 * Tax ID/VAT), no hay UUID ni timbrado ni catálogos del SAT; moneda y tipo de
 * cambio se capturan (el FIX USD se prellena con la fecha de la factura y se
 * puede corregir). Los conceptos son opcionales y se capturan renglón por renglón.
 */
type Concepto = { descripcion: string; cantidad: string; unidad: string; valor_unitario: string };

const MONEDAS = ["USD", "EUR", "CAD", "GBP", "MXN"];
const PAISES = [
  "Estados Unidos", "Canadá", "España", "Alemania", "Reino Unido", "Francia", "Países Bajos",
  "Suiza", "Italia", "Chile", "Colombia", "Brasil", "Guatemala", "Panamá", "Otro",
];

const vacio = (): Concepto => ({ descripcion: "", cantidad: "1", unidad: "SERVICIO", valor_unitario: "" });
const n = (s: string) => {
  const v = Number(String(s).replace(/,/g, ""));
  return Number.isFinite(v) ? v : 0;
};
const fmt = (v: number, d = 2) =>
  new Intl.NumberFormat("es-MX", { minimumFractionDigits: d, maximumFractionDigits: d }).format(v);

export function FacturaExtranjeraForm({ onCargada }: { onCargada: () => void }) {
  const pdfRef = useRef<HTMLInputElement>(null);
  const [emisor, setEmisor] = useState("");
  const [pais, setPais] = useState("Estados Unidos");
  const [paisOtro, setPaisOtro] = useState("");
  const [taxId, setTaxId] = useState("");
  const [serie, setSerie] = useState("");
  const [folio, setFolio] = useState("");
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
  const [moneda, setMoneda] = useState("USD");
  const [tipoCambio, setTipoCambio] = useState("");
  const [fixInfo, setFixInfo] = useState<string | null>(null);
  const [subtotal, setSubtotal] = useState("");
  const [impuestos, setImpuestos] = useState("0");
  const [totalManual, setTotalManual] = useState<string | null>(null);
  const [condiciones, setCondiciones] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [conceptos, setConceptos] = useState<Concepto[]>([vacio()]);
  const [pdfNombre, setPdfNombre] = useState<string | null>(null);
  const [subiendo, setSubiendo] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; texto: string } | null>(null);

  // FIX USD del día de la factura → tipo de cambio sugerido (editable).
  useEffect(() => {
    if (moneda !== "USD" || !fecha) {
      setFixInfo(null);
      return;
    }
    let vivo = true;
    getFixFecha(fecha)
      .then((r) => {
        if (!vivo) return;
        if (r.valor != null) {
          setTipoCambio((t) => (t ? t : r.valor!.toFixed(4)));
          setFixInfo(`FIX Banxico del ${r.fecha}: ${r.valor!.toFixed(4)}`);
        } else setFixInfo("Sin FIX publicado para esa fecha");
      })
      .catch(() => vivo && setFixInfo(null));
    return () => {
      vivo = false;
    };
  }, [fecha, moneda]);

  // Los conceptos con importe alimentan el subtotal si el usuario no lo escribió a mano.
  const importes = useMemo(
    () => conceptos.map((c) => n(c.cantidad) * n(c.valor_unitario)),
    [conceptos],
  );
  const sumaConceptos = importes.reduce((a, b) => a + b, 0);
  const subtotalNum = subtotal.trim() ? n(subtotal) : sumaConceptos;
  const impuestosNum = n(impuestos);
  const totalNum = totalManual != null && totalManual.trim() ? n(totalManual) : subtotalNum + impuestosNum;
  const descuadre = Math.abs(subtotalNum + impuestosNum - totalNum) > 0.05;

  const setConcepto = (i: number, k: keyof Concepto, v: string) =>
    setConceptos((prev) => prev.map((c, j) => (j === i ? { ...c, [k]: v } : c)));

  const listo =
    emisor.trim() && (pais !== "Otro" || paisOtro.trim()) && fecha && moneda && totalNum > 0 &&
    !descuadre && !!pdfNombre;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const pdf = pdfRef.current?.files?.[0];
    if (!pdf) {
      setMsg({ ok: false, texto: "Adjunta el PDF de la factura." });
      return;
    }
    const fd = new FormData();
    fd.append("pdf", pdf);
    fd.append("emisor_nombre", emisor.trim());
    fd.append("emisor_pais", pais === "Otro" ? paisOtro.trim() : pais);
    if (taxId.trim()) fd.append("emisor_tax_id", taxId.trim());
    if (serie.trim()) fd.append("serie", serie.trim());
    if (folio.trim()) fd.append("folio", folio.trim());
    fd.append("fecha", fecha);
    fd.append("moneda", moneda);
    if (tipoCambio.trim()) fd.append("tipo_cambio", String(n(tipoCambio)));
    fd.append("subtotal", subtotalNum.toFixed(2));
    fd.append("total_impuestos", impuestosNum.toFixed(2));
    fd.append("total", totalNum.toFixed(2));
    if (condiciones.trim()) fd.append("condiciones_pago", condiciones.trim());
    if (descripcion.trim()) fd.append("descripcion", descripcion.trim());
    const lineas = conceptos
      .filter((c) => c.descripcion.trim())
      .map((c, i) => ({
        descripcion: c.descripcion.trim(),
        cantidad: n(c.cantidad),
        unidad: c.unidad.trim() || null,
        valor_unitario: n(c.valor_unitario),
        importe: Number(importes[i].toFixed(2)),
      }));
    if (lineas.length) fd.append("conceptos", JSON.stringify(lineas));

    setSubiendo(true);
    setMsg(null);
    try {
      const f = await subirFacturaExtranjera(fd);
      setMsg({ ok: true, texto: `Factura #${f.id} de ${f.emisor_nombre} cargada (${moneda} ${fmt(totalNum)}).` });
      onCargada();
      // Limpia lo propio de la factura; conserva país y moneda para la siguiente.
      setEmisor(""); setTaxId(""); setSerie(""); setFolio(""); setSubtotal(""); setImpuestos("0");
      setTotalManual(null); setCondiciones(""); setDescripcion(""); setConceptos([vacio()]);
      setTipoCambio(""); setPdfNombre(null);
      if (pdfRef.current) pdfRef.current.value = "";
    } catch (err) {
      setMsg({ ok: false, texto: err instanceof Error ? err.message : "No se pudo cargar la factura." });
    } finally {
      setSubiendo(false);
    }
  };

  const inp = "border rounded px-2 py-1.5 text-sm w-full";
  const lbl = "block text-xs text-slate-500 mb-1";

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <p className="text-xs text-slate-500">
        Para facturas emitidas fuera de México, sin CFDI. Se guarda con origen <b>Extranjera</b>;
        no lleva UUID, timbrado ni claves del SAT. El PDF de la factura es obligatorio.
      </p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <label className="text-sm lg:col-span-2">
          <span className={lbl}>Emisor (razón social) *</span>
          <input className={inp} value={emisor} onChange={(e) => setEmisor(e.target.value)} placeholder="p. ej. ACME Energy LLC" />
        </label>
        <label className="text-sm">
          <span className={lbl}>País *</span>
          <select className={inp} value={pais} onChange={(e) => setPais(e.target.value)}>
            {PAISES.map((p) => <option key={p}>{p}</option>)}
          </select>
        </label>
        {pais === "Otro" ? (
          <label className="text-sm">
            <span className={lbl}>¿Cuál? *</span>
            <input className={inp} value={paisOtro} onChange={(e) => setPaisOtro(e.target.value)} />
          </label>
        ) : (
          <label className="text-sm">
            <span className={lbl}>Tax ID / VAT (equivale al RFC)</span>
            <input className={inp} value={taxId} onChange={(e) => setTaxId(e.target.value)} placeholder="p. ej. 12-3456789" />
          </label>
        )}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-3">
        <label className="text-sm">
          <span className={lbl}>Serie</span>
          <input className={inp} value={serie} onChange={(e) => setSerie(e.target.value)} />
        </label>
        <label className="text-sm">
          <span className={lbl}>Número de factura</span>
          <input className={inp} value={folio} onChange={(e) => setFolio(e.target.value)} placeholder="INV-1023" />
        </label>
        <label className="text-sm">
          <span className={lbl}>Fecha de emisión *</span>
          <input type="date" className={inp} value={fecha} onChange={(e) => setFecha(e.target.value)} />
        </label>
        <label className="text-sm">
          <span className={lbl}>Moneda *</span>
          <select className={inp} value={moneda} onChange={(e) => { setMoneda(e.target.value); setTipoCambio(""); }}>
            {MONEDAS.map((m) => <option key={m}>{m}</option>)}
          </select>
        </label>
        <label className="text-sm lg:col-span-2">
          <span className={lbl}>Tipo de cambio a MXN {fixInfo && <span className="text-slate-400">· {fixInfo}</span>}</span>
          <input className={inp} value={tipoCambio} onChange={(e) => setTipoCambio(e.target.value)} placeholder={moneda === "MXN" ? "no aplica" : "p. ej. 17.2450"} disabled={moneda === "MXN"} />
        </label>
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-medium text-slate-600">Conceptos (opcional)</span>
          <button type="button" onClick={() => setConceptos((p) => [...p, vacio()])} className="inline-flex items-center gap-1 text-xs text-blue-700 hover:underline">
            <Plus className="w-3.5 h-3.5" /> Agregar renglón
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="text-xs border rounded bg-white min-w-[40rem] w-full">
            <thead>
              <tr className="text-left text-slate-500 border-b bg-slate-50">
                <th className="px-2 py-1 w-8">#</th>
                <th className="px-2 py-1">Descripción</th>
                <th className="px-2 py-1 w-24 text-right">Cantidad</th>
                <th className="px-2 py-1 w-28">Unidad</th>
                <th className="px-2 py-1 w-32 text-right">P. unitario</th>
                <th className="px-2 py-1 w-32 text-right">Importe</th>
                <th className="px-2 py-1 w-8" />
              </tr>
            </thead>
            <tbody>
              {conceptos.map((c, i) => (
                <tr key={i} className="border-b last:border-0">
                  <td className="px-2 py-1 text-slate-400">{i + 1}</td>
                  <td className="px-2 py-1"><input className="border rounded px-1.5 py-1 w-full" value={c.descripcion} onChange={(e) => setConcepto(i, "descripcion", e.target.value)} placeholder="p. ej. Consultoría septiembre" /></td>
                  <td className="px-2 py-1"><input className="border rounded px-1.5 py-1 w-full text-right" value={c.cantidad} onChange={(e) => setConcepto(i, "cantidad", e.target.value)} /></td>
                  <td className="px-2 py-1"><input className="border rounded px-1.5 py-1 w-full" value={c.unidad} onChange={(e) => setConcepto(i, "unidad", e.target.value)} /></td>
                  <td className="px-2 py-1"><input className="border rounded px-1.5 py-1 w-full text-right" value={c.valor_unitario} onChange={(e) => setConcepto(i, "valor_unitario", e.target.value)} /></td>
                  <td className="px-2 py-1 text-right tabular-nums font-medium">{fmt(importes[i])}</td>
                  <td className="px-2 py-1">
                    {conceptos.length > 1 && (
                      <button type="button" onClick={() => setConceptos((p) => p.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-600" title="Quitar">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <label className="text-sm">
          <span className={lbl}>Subtotal * <span className="text-slate-400">(vacío = suma de conceptos)</span></span>
          <input className={`${inp} text-right`} value={subtotal} onChange={(e) => setSubtotal(e.target.value)} placeholder={fmt(sumaConceptos)} />
        </label>
        <label className="text-sm">
          <span className={lbl}>Impuestos</span>
          <input className={`${inp} text-right`} value={impuestos} onChange={(e) => setImpuestos(e.target.value)} />
        </label>
        <label className="text-sm">
          <span className={lbl}>Total * <span className="text-slate-400">(vacío = subtotal + impuestos)</span></span>
          <input className={`${inp} text-right font-medium ${descuadre ? "border-red-400" : ""}`} value={totalManual ?? ""} onChange={(e) => setTotalManual(e.target.value)} placeholder={fmt(subtotalNum + impuestosNum)} />
          {descuadre && <span className="text-xs text-red-600">Subtotal + impuestos no cuadra con el total.</span>}
        </label>
        <label className="text-sm lg:col-span-2">
          <span className={lbl}>Condiciones de pago</span>
          <input className={inp} value={condiciones} onChange={(e) => setCondiciones(e.target.value)} placeholder="p. ej. Transferencia, 30 días" />
        </label>
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <label className="text-sm">
          <span className={lbl}>PDF de la factura *</span>
          <input
            ref={pdfRef}
            type="file"
            accept=".pdf,application/pdf"
            onChange={() => setPdfNombre(pdfRef.current?.files?.[0]?.name ?? null)}
            className="block text-sm text-slate-600 file:mr-3 file:rounded file:border-0 file:bg-blue-50 file:px-3 file:py-1.5 file:text-sm file:text-blue-700 hover:file:bg-blue-100"
          />
        </label>
        <label className="text-sm flex-1 min-w-[16rem]">
          <span className={lbl}>Notas (opcional)</span>
          <input className={inp} value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />
        </label>
        <button
          type="submit"
          disabled={!listo || subiendo}
          title={listo ? undefined : "Faltan datos obligatorios (*) o el PDF"}
          className="inline-flex items-center gap-2 bg-blue-600 text-white rounded px-4 py-2 text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Upload className="w-4 h-4" /> {subiendo ? "Cargando…" : "Cargar factura extranjera"}
        </button>
      </div>

      {msg && (
        <div className={`text-sm rounded p-2.5 border ${msg.ok ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-red-50 border-red-200 text-red-700"}`}>
          {msg.texto}
        </div>
      )}
    </form>
  );
}
